import React, { useEffect, useState } from "react";
import { Box, IconButton, Tooltip, CircularProgress, TextField, Button, Stepper, Step, StepLabel, Autocomplete, Switch, FormControlLabel, Typography, Divider, MenuItem, Select, InputLabel, FormControl, styled, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AddCircle, ChevronLeft, ChevronRight, Delete } from "@mui/icons-material";
import Datagrid from "../Datagrid/Datagrid";
import { LABELS, TABLE_HEADERS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import Swal from "sweetalert2";
import {
    getAllCustomerSites,
    updateSite,
    createSite,
    deleteSites,
    downloadSiteTemplate,
    bulkUploadSites
} from "../../services/manageSitesMasterService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getCustomSitesData, updateCustomerSites } from "../../services/manageSitesService";
import { useOptions } from "../../context/optionContext";
import { getCustomerLocationOptionFlags, resolveServiceOptions } from "../../utilities/customerLocationSettings";

const GroupHeader = styled("div")(() => ({
    position: "sticky",
    top: 0,
    padding: "4px 8px",
    color: "#181818",
    backgroundColor: "#eee",
    fontWeight: 600,
    fontSize: "12.5px",
}));

const GroupItems = styled("ul")({
    padding: 0,
    margin: 0,
    listStyle: "none",
});
const ManageSitesMaster = () => {
    const [allSites, setAllSites] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selection, setSelection] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [formAlert, setFormAlert] = useState(null);
    const [form, setForm] = useState({
        cus_id: "",
        customer_number: "",
        customer_site_number: "",
        customer_site_name: "",
        ccr_name: "",
        ccr_email: "",
        ccr_phone: "",
        accr_name: "",
        accr_phone: "",
        accr_email: "",
        invoice_account: "",
        amf_method_username_ln: "",
        amf_method_password_ln: "",
        address_name_ln: "",
        address_full_ln: "",
        street1_ln: "",
        street2_ln: "",
        street3_ln: "",
        city_ln: "",
        county_ln: "",
        zip_ln: "",
        state_ln: "",
        country_ln: "",
        po_box_ln: "",
        delivery_terms: "",
        site_legal_entity: "",
        currency_code: "",
        exchange_rate: "",
        site_status: "",
        discount_code: "",
        payment_terms: "",
        site_name: "",
        warehouse: "",
    });
    const { optionKeys } = useOptions();
    const serviceOptions = React.useMemo(() => resolveServiceOptions(optionKeys), [optionKeys]);
    const {
        servicesEnabled,
        showLegalEntity,
        showSite,
        showWarehouse,
        showPaymentTerms,
        showOnHold,
    } = getCustomerLocationOptionFlags(optionKeys);
    const hasServiceDefinitions = servicesEnabled && serviceOptions.length > 0;
    const [serviceMeta, setServiceMeta] = useState(() =>
        serviceOptions.reduce((acc, service) => {
            if (service?.key) acc[service.key] = false;
            return acc;
        }, {})
    );
    const { t } = useTranslation();
    const steps = [t('GENERAL.SITE_DETAILS'), t('BUTTON.OPTIONS')].concat(hasServiceDefinitions ? [t('LABELS.SERVICES')] : []);
    const [shippingSites, setShippingSites] = useState([]);
    const [shippingWarehouses, setShippingWarehouses] = useState([]);
    const [filteredSites, setFilteredSites] = useState([]);
    const [filteredWarehouses, setFilteredWarehouses] = useState([]);

    useEffect(() => {
        const fetchAllSites = async () => {
            try {
                const [sitesResp, customerResp] = await Promise.all([
                    getAllCustomerSites(),
                    getCustomersCusSites(),
                ]);
                setAllSites(sitesResp.data);
                setCustomers(customerResp.data || []);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAllSites();
    }, []);

    useEffect(() => {
        const fetchShippingData = async () => {
            try {
                const [sitesResp, warehousesResp] = await Promise.all([
                    getCustomSitesData("SELECT * FROM shipping_sites"),
                    getCustomSitesData("SELECT * FROM shipping_warehouses"),
                ]);
                setShippingSites(sitesResp?.data || []);
                setShippingWarehouses(warehousesResp?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchShippingData();
    }, []);

    useEffect(() => {
        setServiceMeta(
            serviceOptions.reduce((acc, service) => {
                if (service?.key) acc[service.key] = false;
                return acc;
            }, {})
        );
    }, [serviceOptions]);

    useEffect(() => {
        if (!hasServiceDefinitions && activeStep > 1) {
            setActiveStep(1);
        }
    }, [hasServiceDefinitions, activeStep]);

    useEffect(() => {
        if (form.site_legal_entity) {
            let filteredS = shippingSites.filter(
                (site) => site.data_area_id === form.site_legal_entity
            );
            let filteredW = shippingWarehouses.filter(
                (wh) => wh.data_area_id === form.site_legal_entity
            );

            if (filteredS.length === 0 && filteredW.length === 0) {
                filteredS = shippingSites.filter((s) => s.data_area_id === "AUSA");
                filteredW = shippingWarehouses.filter((w) => w.data_area_id === "AUSA");
            }

            setFilteredSites(filteredS);
            setFilteredWarehouses(filteredW);
        }
    }, [form.site_legal_entity, shippingSites, shippingWarehouses]);

    useEffect(() => {
        if (form.site_name) {
            const selectedSite = shippingSites.find(
                (site) => site.shipping_site_id === form.site_name
            );

            if (selectedSite) {
                const entity = selectedSite.data_area_id;
                setForm((prev) => ({
                    ...prev,
                    site_legal_entity: entity,
                }));

                let filteredW = shippingWarehouses.filter(
                    (wh) => wh.data_area_id === entity
                );

                if (filteredW.length === 0) {
                    filteredW = shippingWarehouses.filter((wh) => wh.data_area_id === "AUSA");
                }

                setFilteredWarehouses(filteredW);
            }
        }
    }, [form.site_name, shippingSites, shippingWarehouses]);

    useEffect(() => {
        if (form.warehouse) {
            const selectedWh = shippingWarehouses.find(
                (wh) => wh.shipping_warehouse_id === form.warehouse
            );

            if (selectedWh) {
                const entity = selectedWh.data_area_id;
                setForm((prev) => ({
                    ...prev,
                    site_legal_entity: entity,
                }));

                let filteredS = shippingSites.filter(
                    (s) => s.data_area_id === entity
                );

                if (filteredS.length === 0) {
                    filteredS = shippingSites.filter((s) => s.data_area_id === "AUSA");
                }

                setFilteredSites(filteredS);
            }
        }
    }, [form.warehouse, shippingWarehouses, shippingSites]);

    const loadSiteMeta = async (siteId) => {
        try {
            const query = `SELECT meta_key, meta_value FROM site_meta WHERE site_id=${siteId}`;
            const resp = await getCustomSitesData(query);
            const rows = resp?.data || resp || [];
            const metaMap = new Map(rows.map((row) => [row.meta_key, row.meta_value]));
            if (metaMap.has("warehouse")) {
                setForm((prev) => ({ ...prev, warehouse: metaMap.get("warehouse") || "" }));
            }
            if (metaMap.has("site_currency")) {
                setForm((prev) => ({ ...prev, currency_code: metaMap.get("site_currency") || prev.currency_code }));
            }
            if (metaMap.has("site_name")) {
                setForm((prev) => ({ ...prev, site_name: metaMap.get("site_name") || "" }));
            }
            if (metaMap.has("site_legal_entity")) {
                setForm((prev) => ({ ...prev, site_legal_entity: metaMap.get("site_legal_entity") || prev.site_legal_entity }));
            }
            const nextServices = { ...serviceMeta };
            serviceOptions.forEach((service) => {
                if (!service?.key) return;
                const value = metaMap.get(service.key);
                nextServices[service.key] = value === "1" || value === "true" || value === "yes";
            });
            setServiceMeta(nextServices);
        } catch (error) {
            console.error(error);
        }
    };

    const handleEdit = (row) => {
        setEditRow(row);
        const customer = customers.find((c) => c.cus_id === row.cus_id) || null;
        setSelectedCustomer(customer);
        setForm({
            cus_id: row.cus_id || "",
            customer_number: row.parent_customer_number || row.customer_number || "",
            customer_site_number: row.customer_site_number || "",
            customer_site_name: row.customer_site_name || "",
            ccr_name: row.ccr_name || "",
            ccr_email: row.ccr_email || "",
            ccr_phone: row.ccr_phone || "",
            accr_name: row.accr_name || "",
            accr_phone: row.accr_phone || "",
            accr_email: row.accr_email || "",
            invoice_account: row.invoice_account || "",
            amf_method_username_ln: row.amf_method_username_ln || "",
            amf_method_password_ln: row.amf_method_password_ln || "",
            address_name_ln: row.address_name_ln || "",
            address_full_ln: row.address_full_ln || "",
            street1_ln: row.street1_ln || "",
            street2_ln: row.street2_ln || "",
            street3_ln: row.street3_ln || "",
            city_ln: row.city_ln || "",
            county_ln: row.county_ln || "",
            zip_ln: row.zip_ln || "",
            state_ln: row.state_ln || "",
            country_ln: row.country_ln || "",
            po_box_ln: row.po_box_ln || "",
            delivery_terms: row.delivery_terms || "",
            site_legal_entity: row.site_legal_entity || "",
            currency_code: row.currency_code ? `${row.currency_code}`.toLowerCase() : "",
            exchange_rate: row.exchange_rate || "",
            site_status: row.site_status || "",
            discount_code: row.discount_code || "",
            payment_terms: row.payment_terms || "",
            site_name: "",
            warehouse: "",
        });
        setFormErrors({});
        setActiveStep(0);
        setFormAlert(null);
        if (row.site_id) {
            loadSiteMeta(row.site_id);
        }
        setDialogOpen(true);
    };

    const handleAdd = () => {
        setEditRow(null);
        setSelectedCustomer(null);
        setForm({
            cus_id: "",
            customer_number: "",
            customer_site_number: "",
            customer_site_name: "",
            ccr_name: "",
            ccr_email: "",
            ccr_phone: "",
            accr_name: "",
            accr_phone: "",
            accr_email: "",
            invoice_account: "",
            amf_method_username_ln: "",
            amf_method_password_ln: "",
            address_name_ln: "",
            address_full_ln: "",
            street1_ln: "",
            street2_ln: "",
            street3_ln: "",
            city_ln: "",
            county_ln: "",
            zip_ln: "",
            state_ln: "",
            country_ln: "",
            po_box_ln: "",
            delivery_terms: "",
            site_legal_entity: "",
            currency_code: "",
            exchange_rate: "",
            site_status: "",
            discount_code: "",
            payment_terms: "",
            site_name: "",
            warehouse: "",
        });
        setServiceMeta(serviceOptions.reduce((acc, service) => {
            if (service?.key) acc[service.key] = false;
            return acc;
        }, {}));
        setFormErrors({});
        setActiveStep(0);
        setFormAlert(null);
        setDialogOpen(true);
    };

    const validateStep = (step) => {
        const nextErrors = {};
        if (step === 0) {
            if (!form.customer_site_number) nextErrors.customer_site_number = t('GENERAL.SITE_NUMBER_IS_REQUIRED');
            if (!form.customer_site_name) nextErrors.customer_site_name = t('FORMS.SITE_NAME_IS_REQUIRED');
            if (!form.cus_id && !form.customer_number) nextErrors.customer_number = t('GENERAL.CUSTOMER_IS_REQUIRED');
        }
        if (step === 1) {
            if (showLegalEntity && !form.site_legal_entity) nextErrors.site_legal_entity = t('GENERAL.LEGAL_ENTITY_REQUIRED');
            if (!form.currency_code) nextErrors.currency_code = t('GENERAL.CURRENCY_CODE_IS_REQUIRED');
            if (showSite && !form.site_name) nextErrors.site_name = t('GENERAL.SITE_REQUIRED');
            if (showWarehouse && !form.warehouse) nextErrors.warehouse = t('GENERAL.WAREHOUSE_REQUIRED');
        }
        setFormErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            setFormAlert({ severity: "warning", message: t('UI_TEXT.PLEASE_FIX_THE_HIGHLIGHTED_FIELDS') });
        } else {
            setFormAlert(null);
        }
        return Object.keys(nextErrors).length === 0;
    };

    const validateAll = () => {
        const nextErrors = {};
        if (!form.customer_site_number) nextErrors.customer_site_number = t('GENERAL.SITE_NUMBER_IS_REQUIRED');
        if (!form.customer_site_name) nextErrors.customer_site_name = t('FORMS.SITE_NAME_IS_REQUIRED');
        if (!form.cus_id && !form.customer_number) nextErrors.customer_number = t('FORMS.CUSTOMER_IS_REQUIRED');
        if (showLegalEntity && !form.site_legal_entity) nextErrors.site_legal_entity = t('GENERAL.LEGAL_ENTITY_REQUIRED');
        if (!form.currency_code) nextErrors.currency_code = t('FORMS.CURRENCY_CODE_IS_REQUIRED');
        if (showSite && !form.site_name) nextErrors.site_name = t('GENERAL.SITE_REQUIRED');
        if (showWarehouse && !form.warehouse) nextErrors.warehouse = t('GENERAL.WAREHOUSE_REQUIRED');
        setFormErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            setFormAlert({ severity: "warning", message: t('UI_TEXT.PLEASE_FIX_THE_HIGHLIGHTED_FIELDS') });
        } else {
            setFormAlert(null);
        }
        return Object.keys(nextErrors).length === 0;
    };

    const handleNext = () => {
        if (!validateStep(activeStep)) return;
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const handleSave = async () => {
        try {
            const isValid = validateAll();
            if (!isValid) {
                Swal.fire({
                    icon: "warning",
                    title: t('ALERT_MESSAGES.WARNING'),
                    text: t('UI_TEXT.PLEASE_FIX_THE_HIGHLIGHTED_FIELDS'),
                });
                return;
            }
            const nextNumber = `${form.customer_site_number || ""}`.trim().toLowerCase();
            const hasDuplicate = allSites.some((site) => {
                if (!site?.customer_site_number) return false;
                if (editRow && site.site_id === editRow.site_id) return false;
                return String(site.customer_site_number).trim().toLowerCase() === nextNumber;
            });
            if (hasDuplicate) {
                setFormAlert({ severity: "error", message: t('GENERAL.SITE_NUMBER_ALREADY_EXISTS') });
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: t('GENERAL.SITE_NUMBER_ALREADY_EXISTS'),
                });
                return;
            }

            const payload = {
                cus_id: form.cus_id || undefined,
                customer_site_name: form.customer_site_name,
                customer_site_number: form.customer_site_number,
                ccr_name: form.ccr_name,
                ccr_email: form.ccr_email,
                ccr_phone: form.ccr_phone,
                accr_name: form.accr_name,
                accr_phone: form.accr_phone,
                accr_email: form.accr_email,
                invoice_account: selectedCustomer?.customer_number || form.customer_number,
                amf_method_username_ln: form.amf_method_username_ln,
                amf_method_password_ln: form.amf_method_password_ln,
                address_name_ln: form.address_name_ln,
                address_full_ln: form.address_full_ln,
                street1_ln: form.street1_ln,
                street2_ln: form.street2_ln,
                street3_ln: form.street3_ln,
                city_ln: form.city_ln,
                county_ln: form.county_ln,
                zip_ln: form.zip_ln,
                state_ln: form.state_ln,
                country_ln: form.country_ln,
                po_box_ln: form.po_box_ln,
                delivery_terms: form.delivery_terms,
                site_legal_entity: form.site_legal_entity,
                currency_code: form.currency_code,
                exchange_rate: form.exchange_rate,
                site_status: form.site_status,
                discount_code: form.discount_code,
                payment_terms: form.payment_terms,
            };

            if (form.customer_number) {
                payload.customer_number = form.customer_number;
            }

            let savedSite = null;
            if (editRow) {
                const resp = await updateSite(editRow.site_id, payload);
                savedSite = resp?.data || editRow;
            } else {
                const resp = await createSite(payload);
                savedSite = resp?.data || resp;
            }

            if (savedSite?.site_id) {
                const resolvedLegalEntity = form.site_legal_entity || "AUSA";
                const resolvedSiteName =
                    form.site_name ||
                    filteredSites[0]?.shipping_site_id ||
                    shippingSites[0]?.shipping_site_id ||
                    "";
                const resolvedWarehouse =
                    form.warehouse ||
                    filteredWarehouses[0]?.shipping_warehouse_id ||
                    shippingWarehouses[0]?.shipping_warehouse_id ||
                    "";

                const servicesPayload = hasServiceDefinitions
                    ? serviceOptions.reduce((acc, service) => {
                        if (!service?.key) return acc;
                        acc[service.key] = serviceMeta[service.key] ? "1" : "0";
                        return acc;
                    }, {})
                    : {};

                await updateCustomerSites({
                    site_id: savedSite.site_id,
                    site_name: resolvedSiteName,
                    warehouse: resolvedWarehouse,
                    site_currency: form.currency_code || "usd",
                    site_legal_entity: resolvedLegalEntity,
                    cus_id: form.cus_id || savedSite.cus_id,
                    discount_code: form.discount_code,
                    services_meta: servicesPayload,
                });
            }

            const resp = await getAllCustomerSites();
            setAllSites(resp.data);
            setDialogOpen(false);
            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: editRow ? t('GENERAL.SITE_UPDATED_SUCCESSFULLY_1') : t('GENERAL.SITE_CREATED_SUCCESSFULLY'),
            });
        } catch (e) {
            console.error(e);
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: t('GENERAL.FAILED_TO_SAVE_SITE'),
            });
        }
    };

    const handleDelete = async () => {
        if (selection.length === 0) return;
        try {
            const confirmation = await Swal.fire({
                title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                text: t('GENERAL.THIS_WILL_DELETE_THE_SITES_PERMANENTLY'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: t('GENERAL.YES_DELETE_IT'),
            });
            if (!confirmation.isConfirmed) return;
            await deleteSites(selection);
            const resp = await getAllCustomerSites();
            setAllSites(resp.data);
            setSelection([]);
            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: t('GENERAL.SITES_DELETED_SUCCESSFULLY'),
            });
        } catch (e) { console.error(e); }
    };

    const handleDownloadTemplate = async () => {
        try {
            const resp = await downloadSiteTemplate();
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', 'sites_template.csv');
            document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) { console.error(e); }
    };

    const handleUploadCSV = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        try {
            await bulkUploadSites(fd);
            const resp = await getAllCustomerSites();
            setAllSites(resp.data);
        } catch (err) { console.error(err); }
    };

    const columns = [
        { field: "customer_site_number", headerName: t('TABLE_HEADERS.SITE_NUMBER'), flex: 1 },
        { field: "customer_site_name", headerName: t('LABELS.SITE_NAME'), flex: 1 },
        { field: "invoice_account", headerName: t('TABLE_HEADERS.INVOICE_ACCOUNT'), flex: 1 },
        { field: "parent_customer_name", headerName: t('TABLE_HEADERS.PARENT_CUSTOMER'), flex: 1 },
        { field: "address_full_ln", headerName: t('TABLE_HEADERS.SITE_ADDRESS'), flex: 1 },
        {
            headerName: t('TABLE_HEADERS.ACTIONS'),
            sortable: false,
            width: 100, // Ensure there's enough space for icons
            headerAlign: "center",
            align: "left",
            renderCell: (params) => (
                <div>
                    <Tooltip title={t('GENERAL.EDIT_SITE')}>
                        <IconButton
                            color="primary"
                            aria-label="Edit Site"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleEdit(params.row);
                            }}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const customButtons = [
        { label: 'Add', icon: <AddCircle />, onClick: handleAdd },
        // { label: 'Download Template', icon: <ChevronRight />, onClick: handleDownloadTemplate },
        // { label: 'Upload CSV', icon: <ChevronLeft />, onClick: () => { }, upload: true, acceptedFile: '.csv', handleFileChange: handleUploadCSV },
        { label: t('LABELS.DELETE_SELECTED'), icon: <Delete />, onClick: handleDelete },
    ];

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Datagrid
                loading={loading}
                data={allSites}
                columns={columns}
                customButtons={customButtons}
                checkboxSelection={true}
                rowIdField="site_id"
                onSelect={setSelection}
                sortBy={[{ field: "site_id", sort: "asc" }]}
                hideFilterFromColumnMenu
                lockActiveColumnInManageColumns
                preventHidingLastColumn
            />
            <ModalWrapper
                open={dialogOpen}
                setOpen={setDialogOpen}
                title={editRow ? t('GENERAL.EDIT_SITE') : t('GENERAL.ADD_SITE')}
                content={
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        {formAlert && (
                            <Alert severity={formAlert.severity || "error"}>{formAlert.message}</Alert>
                        )}
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {activeStep === 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>{t('GENERAL.SITE_DETAILS')}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('GENERAL.PROVIDE_THE_CUSTOMER_AND_CORE_SITE_IDENTIFIERS_ALONG_WITH_CCR_CONTACT_INFO')}
                                    </Typography>
                                </Box>
                                <Autocomplete
                                    options={customers}
                                    getOptionLabel={(option) => option.customer_name || ""}
                                    value={selectedCustomer}
                                    onChange={(event, newValue) => {
                                        setSelectedCustomer(newValue);
                                        setForm((prev) => ({
                                            ...prev,
                                            cus_id: newValue?.cus_id || "",
                                            customer_number: newValue?.customer_number || "",
                                        }));
                                        setFormErrors((prev) => ({ ...prev, customer_number: "" }));
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('TABLE_HEADERS.CUSTOMER_NAME')}
                                            error={Boolean(formErrors.customer_number)}
                                            helperText={formErrors.customer_number || ""}
                                            size="small"
                                        />
                                    )}
                                />
                                <TextField
                                    label={t('TABLE_HEADERS.CUSTOMER_NUMBER')}
                                    value={form.customer_number}
                                    onChange={e => setForm({ ...form, customer_number: e.target.value })}
                                    error={Boolean(formErrors.customer_number)}
                                    helperText={formErrors.customer_number || ""}
                                    size="small"
                                />
                                <TextField
                                    label={t('TABLE_HEADERS.SITE_NUMBER')}
                                    value={form.customer_site_number}
                                    onChange={e => setForm({ ...form, customer_site_number: e.target.value })}
                                    error={Boolean(formErrors.customer_site_number)}
                                    helperText={formErrors.customer_site_number || ""}
                                    size="small"
                                />
                                <TextField
                                    label={t('LABELS.SITE_NAME')}
                                    value={form.customer_site_name}
                                    onChange={e => setForm({ ...form, customer_site_name: e.target.value })}
                                    error={Boolean(formErrors.customer_site_name)}
                                    helperText={formErrors.customer_site_name || ""}
                                    size="small"
                                />
                                <Divider />
                                <Box>

                                    <Typography variant="subtitle1" fontWeight={600}>{t('LABELS.CCR_CONTACT')}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('GENERAL.PRIMARY_CONTACT_FOR_THIS_SITE')}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <TextField label={t('TABLE_HEADERS.NAME')} value={form.ccr_name} onChange={e => setForm({ ...form, ccr_name: e.target.value })} fullWidth size="small" />
                                    <TextField label={t('TABLE_HEADERS.EMAIL')} value={form.ccr_email} onChange={e => setForm({ ...form, ccr_email: e.target.value })} fullWidth size="small" />
                                    <TextField label={t('LABELS.PHONE')} value={form.ccr_phone} onChange={e => setForm({ ...form, ccr_phone: e.target.value })} fullWidth size="small" />
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>{t('LABELS.ADDRESS')}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('GENERAL.DEFAULT_ADDRESS_FOR_THIS_SITE')}
                                    </Typography>
                                </Box>
                                <TextField label={t('UI_TEXT.ADDRESS_NAME')} value={form.address_name_ln} onChange={e => setForm({ ...form, address_name_ln: e.target.value })} size="small" />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField label={t('LABELS.STREET')} value={form.street1_ln} onChange={e => setForm({ ...form, street1_ln: e.target.value })} size="small" fullWidth />
                                    <TextField label={t('GENERAL.STREET_2')} value={form.street2_ln} onChange={e => setForm({ ...form, street2_ln: e.target.value })} size="small" fullWidth />
                                    <TextField label={t('GENERAL.STREET_3')} value={form.street3_ln} onChange={e => setForm({ ...form, street3_ln: e.target.value })} size="small" fullWidth />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField label={t('LABELS.COUNTY')} value={form.county_ln} onChange={e => setForm({ ...form, county_ln: e.target.value })} size="small" fullWidth />
                                    <TextField label={t('LABELS.CITY')} value={form.city_ln} onChange={e => setForm({ ...form, city_ln: e.target.value })} size="small" fullWidth />
                                    <TextField label={t('LABELS.STATE')} value={form.state_ln} onChange={e => setForm({ ...form, state_ln: e.target.value })} size="small" fullWidth />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField label={t('LABELS.ZIP')} value={form.zip_ln} onChange={e => setForm({ ...form, zip_ln: e.target.value })} size="small" fullWidth />
                                    <TextField label={t('LABELS.COUNTRY')} value={form.country_ln} onChange={e => setForm({ ...form, country_ln: e.target.value })} size="small" fullWidth />
                                    <TextField label={t('GENERAL.PO_BOX')} value={form.po_box_ln} onChange={e => setForm({ ...form, po_box_ln: e.target.value })} size="small" fullWidth />
                                </Box>
                            </Box>
                        )}

                        {activeStep === 1 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>Options</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('GENERAL.CONFIGURE_FULFILLMENT_AND_FINANCE_SETTINGS_FOR_THE_SITE')}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 2 }}>
                                    {showLegalEntity && (
                                        <FormControl size="small" fullWidth error={Boolean(formErrors.site_legal_entity)}>
                                            <InputLabel>Legal Entity</InputLabel>
                                            <Select
                                                label="Legal Entity"
                                                value={form.site_legal_entity || ""}
                                                onChange={(e) => {
                                                    setForm((prev) => ({ ...prev, site_legal_entity: e.target.value }));
                                                    setFormErrors((prev) => ({ ...prev, site_legal_entity: "" }));
                                                }}
                                            >
                                                <MenuItem value="AUSA">AUSA</MenuItem>
                                                <MenuItem value="ACAN">ACAN</MenuItem>
                                                <MenuItem value="EUUK">EUUK</MenuItem>
                                                <MenuItem value="SING">SING</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                    <FormControl size="small" fullWidth error={Boolean(formErrors.currency_code)}>
                                        <InputLabel>Site Currency</InputLabel>
                                        <Select
                                            label="Site Currency"
                                            value={form.currency_code || ""}
                                            onChange={(e) => {
                                                setForm((prev) => ({ ...prev, currency_code: e.target.value }));
                                                setFormErrors((prev) => ({ ...prev, currency_code: "" }));
                                            }}
                                        >
                                            <MenuItem value="usd">USD</MenuItem>
                                            <MenuItem value="cad">CAD</MenuItem>
                                            <MenuItem value="gbp">GBP</MenuItem>
                                            <MenuItem value="eur">EUR</MenuItem>
                                            <MenuItem value="sgd">SGD</MenuItem>
                                            <MenuItem value="inr">INR</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                {(showSite || showWarehouse) && (
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                        {showSite && (
                                            <Autocomplete
                                                fullWidth
                                                options={filteredSites.map((site) => ({
                                                    id: site.shipping_site_id,
                                                    name: site.shipping_site_name,
                                                    data_area_id: site.data_area_id,
                                                }))}
                                                groupBy={(option) => option.data_area_id}
                                                getOptionLabel={(option) => option?.id || ""}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                value={
                                                    filteredSites
                                                        .map((s) => ({
                                                            id: s.shipping_site_id,
                                                            name: s.shipping_site_name,
                                                            data_area_id: s.data_area_id,
                                                        }))
                                                        .find((s) => s.id === form.site_name) || null
                                                }
                                                onChange={(event, newValue) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        site_name: newValue ? newValue.id : "",
                                                    }));
                                                    setFormErrors((prev) => ({ ...prev, site_name: "" }));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={t('SITE_MANAGEMENT.SITE_1')}
                                                        size="small"
                                                        fullWidth
                                                        error={Boolean(formErrors.site_name)}
                                                        helperText={formErrors.site_name || ""}
                                                    />
                                                )}
                                                renderGroup={(params) => (
                                                    <li key={params.key}>
                                                        <GroupHeader>{params.group}</GroupHeader>
                                                        <GroupItems>{params.children}</GroupItems>
                                                    </li>
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props} style={{ width: "100%" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "flex-start",
                                                                width: "100%",
                                                                px: 0.5,
                                                                py: 0.5,
                                                            }}
                                                        >
                                                            <Typography variant="body1" fontSize={"13px"} fontWeight={"bold"}>
                                                                {option.id}
                                                            </Typography>
                                                            <Typography variant="body1" fontSize={"12px"} color="text.secondary">
                                                                {option.name}
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                            />
                                        )}
                                        {showWarehouse && (
                                            <Autocomplete
                                                fullWidth
                                                options={filteredWarehouses.map((wh) => ({
                                                    id: wh.shipping_warehouse_id,
                                                    name: wh.shipping_warehouse_name,
                                                    data_area_id: wh.data_area_id,
                                                }))}
                                                groupBy={(option) => option.data_area_id}
                                                getOptionLabel={(option) => option?.id || ""}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                value={
                                                    filteredWarehouses
                                                        .map((w) => ({
                                                            id: w.shipping_warehouse_id,
                                                            name: w.shipping_warehouse_name,
                                                            data_area_id: w.data_area_id,
                                                        }))
                                                        .find((w) => w.id === form.warehouse) || null
                                                }
                                                onChange={(event, newValue) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        warehouse: newValue ? newValue.id : "",
                                                    }));
                                                    setFormErrors((prev) => ({ ...prev, warehouse: "" }));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={t('LABELS.WAREHOUSE')}
                                                        size="small"
                                                        fullWidth
                                                        error={Boolean(formErrors.warehouse)}
                                                        helperText={formErrors.warehouse || ""}
                                                    />
                                                )}
                                                renderGroup={(params) => (
                                                    <li key={params.key}>
                                                        <GroupHeader>{params.group}</GroupHeader>
                                                        <GroupItems>{params.children}</GroupItems>
                                                    </li>
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props} style={{ width: "100%" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "flex-start",
                                                                width: "100%",
                                                                px: 0.5,
                                                                py: 0.5,
                                                            }}
                                                        >
                                                            <Typography variant="body1" fontSize={"13px"} fontWeight={"bold"}>
                                                                {option.id}
                                                            </Typography>
                                                            <Typography variant="body1" fontSize={"12px"} color="text.secondary">
                                                                {option.name}
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                            />
                                        )}
                                    </Box>
                                )}
                                {(showOnHold || showPaymentTerms) && (
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                        {showOnHold && (
                                            <TextField
                                                label={t('GENERAL.ON_HOLD_STATUS')}
                                                fullWidth
                                                value={form.site_status}
                                                size="small"
                                                disabled
                                            />
                                        )}
                                        {showPaymentTerms && (
                                            <TextField
                                                label={t('GENERAL.PAYMENT_TERMS')}
                                                fullWidth
                                                value={form.payment_terms}
                                                size="small"
                                                disabled
                                            />
                                        )}
                                    </Box>
                                )}
                                <TextField
                                    label={t('GENERAL.DISCOUNT_MARKUP_PERCENT')}
                                    fullWidth
                                    type="number"
                                    inputProps={{ step: 0.01 }}
                                    value={form.discount_code}
                                    onChange={e => setForm({ ...form, discount_code: e.target.value })}
                                    helperText={formErrors.discount_code || t('GENERAL.ENTER_DISCOUNT_MARKUP_PERCENTAGE')}
                                    size="small"
                                />
                            </Box>
                        )}

                        {hasServiceDefinitions && activeStep === 2 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>{t('LABELS.SERVICES')}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Toggle site-level service availability.
                                    </Typography>
                                </Box>
                                {serviceOptions.map((service) => (
                                    <FormControlLabel
                                        key={service.key}
                                        control={
                                            <Switch
                                                checked={Boolean(serviceMeta[service.key])}
                                                onChange={() => setServiceMeta((prev) => ({ ...prev, [service.key]: !prev[service.key] }))}
                                            />
                                        }
                                        label={service.label}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                }
                actions={
                    <>
                        <Button onClick={() => setDialogOpen(false)}>{t('BUTTON.CANCEL')}</Button>
                        <Button onClick={handleBack} disabled={activeStep === 0}>Back</Button>
                        {activeStep < steps.length - 1 ? (
                            <Button variant="contained" onClick={handleNext}>Next</Button>
                        ) : (
                            <Button variant="contained" onClick={handleSave}>{t('BUTTON.SAVE')}</Button>
                        )}
                    </>
                }
            />
        </div>
    );
};

export default ManageSitesMaster;